import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Flex,
  Text,
  IconButton,
  Icon,
  Input,
  InputGroup,
  Tooltip,
  Button,
  Switch,
} from "@chakra-ui/react";
import { FaXmark, FaMinus, FaPlus, FaCircleQuestion } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { TbPercentage } from "react-icons/tb";
import { motion } from "framer-motion";
import DropdownButtonNew from "../buttons/DropdownButtonNew";
import TeethPopoverNew from "./TeethPopoverNew";
import TeethPopoverNewMobile from "./TeethPopoverNewMobile";
import {
  textPrimary,
  white,
  backgroundLight,
  primaryAlt,
  newBorder,
  textSecondary,
  color6,
  color8,
  whiteAlt,
  primary,
} from "../../utils/colors";
import useCurrencySymbol from "../../hooks/useCurrencySymbol";
import useIsMobile from "../../hooks/useIsMobile";
import FormControlReimbursementType from "../input/FormControlReimbursementType";
import FormControlDefault from "../input/FormControlDefault";

const PriceQuantityPopoverModal = ({
  isOpen,
  handleClose,
  service,
  saveEditedService,
  isPriceVariations,
  currency,
  tenant,
}) => {
  const { t } = useTranslation();

  // STATES
  const [operatingCountry, setOperatingCountry] = useState(
    tenant?.operatingCountry || "US"
  );
  const [qty, setQty] = useState(service.quantity);
  const [price, setPrice] = useState(service.price);
  const [discount, setDiscount] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(price);
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);
  const [isDiscountMenuOpen, setIsDiscountMenuOpen] = useState(false);
  const [isPriceEditable, setIsPriceEditable] = useState(true);
  const [tempQty, setTempQty] = useState(1);
  const [selectedTeeth, setSelectedTeeth] = useState([]);

  const [isReimbursement, setIsReimbursement] = useState(false);
  const [reimbursementType, setReimbursementType] = useState("public");
  const [reimbursementName, setReimbursementName] = useState("");
  const [reimbursementCode, setReimbursementCode] = useState("");
  const [reimbursementBand, setReimbursementBand] = useState("");
  // let [reimbursedAmount, setReimbursedAmount] = useState(0);
  const [reimbursementCoverageType, setReimbursementCoverageType] =
    useState("");
  const [reimbursementCoverage, setReimbursementCoverage] = useState(0);

  const [namePlaceholder, setNamePlaceholder] = useState("");
  const [codePlaceholder, setCodePlaceholder] = useState("");

  const isMobile = useIsMobile();

  let reimbursedAmount = 0;

  // console.log("service", service);

  const dropdownOptions = [
    {
      id: 1,
      name: "percentage",
      label: t("service.discountPercentage"),
    },
    {
      id: 2,
      name: "amount",
      label: t("service.discountAmount"),
    },
  ];
  const [selectedDiscount, setSelectedDiscount] = useState(dropdownOptions[0]);
  const [isServicePriceVariations, setIsServicePriceVariations] =
    useState(false);

  const reimbursementDropdownOptions = [
    {
      id: 1,
      name: "percent",
      label: t("reimbursement.coveragePercentage"),
    },
    {
      id: 2,
      name: "fixed",
      label: t("reimbursement.coverageAmount"),
    },
  ];
  const [selectedReimbursementCoverage, setSelectedReimbursementCoverage] =
    useState(reimbursementDropdownOptions[0]);

  // SUBMIT

  const handleSubmitChanges = () => {
    // Ensure finalPrice is treated as a number
    const finalPrice =
      discount > 0 ? parseFloat(discountedPrice) : parseFloat(price);

    const countTeeth = selectedTeeth.length;

    let isServiceWithDiscount = "";
    if (discount > 0) {
      isServiceWithDiscount = true;
    } else {
      isServiceWithDiscount = false;
    }

    const finalDiscountValue = parseFloat(discount);

    setQty(tempQty);
    // Save the updated list
    saveEditedService({
      ...service,
      teeth: isServicePriceVariations ? selectedTeeth : [],
      quantity: isServicePriceVariations ? countTeeth : tempQty,
      price: price,
      totalPrice: isServicePriceVariations
        ? countTeeth * finalPrice
        : tempQty * finalPrice,
      discountValue: isServiceWithDiscount ? finalDiscountValue : 0,
      discountType: isServiceWithDiscount ? selectedDiscount.name : "none",
      discountedPrice: discountedPrice,
      // ✅ New reimbursement object
      reimbursement: isReimbursement
        ? {
            type: reimbursementType, // "public" or "insurance"
            name: reimbursementName,
            code: reimbursementCode,
            band: reimbursementBand || null, // optional
            coverageType:
              selectedReimbursementCoverage.name === "percent"
                ? "percent"
                : "fixed",
            coverage: reimbursementCoverage,
            reimbursedAmount: parseFloat(computedReimbursedAmount.toFixed(2)),
            claimRequired: false, // optional logic later
            claimReference: null,
          }
        : null,
    });
    console.log("Edited service saved:", {
      ...service,
      teeth: isServicePriceVariations ? selectedTeeth : [],
      quantity: isServicePriceVariations ? countTeeth : tempQty,
      price: price,
      totalPrice: isServicePriceVariations
        ? countTeeth * finalPrice
        : tempQty * finalPrice,
      discountValue: isServiceWithDiscount ? finalDiscountValue : 0,
      discountType: isServiceWithDiscount ? selectedDiscount.name : "none",
      discountedPrice: discountedPrice,
      reimbursement: isReimbursement
        ? {
            type: reimbursementType, // "public" or "insurance"
            name: reimbursementName,
            code: reimbursementCode,
            band: reimbursementBand || null, // optional
            coverageType:
              selectedReimbursementCoverage.name === "percent"
                ? "percent"
                : "fixed",

            coverage: reimbursementCoverage,
            reimbursedAmount: parseFloat(computedReimbursedAmount.toFixed(2)),
            claimRequired: false, // optional logic later
            claimReference: null,
          }
        : null,
    });

    setIsDiscountOpen(false);
    handleClose(); // Close the popover after confirming the changes
  };

  // TEETH
  const handleToothClick = (toothNumber, event) => {
    event.stopPropagation(); // Stop event from bubbling up
    const updatedSelectedTeeth = selectedTeeth.includes(toothNumber)
      ? selectedTeeth.filter((t) => t !== toothNumber) // Deselect if already selected
      : [...selectedTeeth, toothNumber]; // Select if not already selected
    setSelectedTeeth(updatedSelectedTeeth);
  };

  // QUANTITY
  const incrementQty = () => {
    setTempQty((prevQty) => (prevQty ? prevQty + 1 : 1));
  };

  const decrementQty = () => {
    setTempQty((prevQty) => (prevQty && prevQty > 1 ? prevQty - 1 : 1));
  };

  // PRICE
  const handlePriceChange = (e) => {
    const inputValue = e.target.value;
    // Allow only numbers or empty input for deletion
    if (/^\d*\.?\d*$/.test(inputValue)) {
      const newPrice = inputValue === "" ? 0 : Number(inputValue); // Convert to number or 0
      setPrice(newPrice);

      setDiscountedPrice(newPrice); // Set the discounted price to the new price

      if (selectedDiscount && selectedDiscount.id === 1) {
        // Percentage discount
        setDiscountedPrice(newPrice - (discount / 100) * newPrice); // Use newPrice directly

        // console.log("SKAICIAVAIMAS", newPrice - (discount / 100) * newPrice);
      } else if (selectedDiscount && selectedDiscount.id === 2) {
        // Fixed amount discount
        setDiscountedPrice(newPrice - discount); // Use newPrice directly
      }
    }
  };

  // DISCOUNT

  const discountedAmount = (price, discount) => {
    return (price * (discount / 100)).toFixed(2);
  };

  const discountedDeductAmount = (price, discountedPrice, discount) => {
    // price minues the discount value
    // console.log("price", price);
    // console.log("discountedPrice", discountedPrice);
    // console.log("discount", discount);
    // console.log("price - discount", price - discount);
    return (price - discountedPrice).toFixed(2);
  };

  const handleDiscountChange = (e) => {
    const inputValue = e.target.value;

    // Allow only numbers or empty input for deletion
    if (/^\d*$/.test(inputValue)) {
      const newDiscount = inputValue === "" ? 0 : Number(inputValue); // Convert to number or 0
      setDiscount(newDiscount);

      if (selectedDiscount && selectedDiscount.id === 1) {
        // Percentage discount
        setDiscountedPrice(price - (newDiscount / 100) * price);
      } else if (selectedDiscount && selectedDiscount.id === 2) {
        // Fixed amount discount
        setDiscountedPrice(price - newDiscount);
      } else {
        setDiscountedPrice(0); // No discount case
      }
    }
  };

  const handleDropdownSelect = (option) => {
    const selectedOption = dropdownOptions.find(
      (item) => item.label === option
    );
    setSelectedDiscount(selectedOption);
    setIsDiscountMenuOpen
      ? setIsDiscountMenuOpen(false)
      : setIsDiscountMenuOpen(true);

    if (selectedOption.name === "none") {
      setDiscount(0);
    } else {
      setDiscount(0);
    }
  };

  const handleReimbursementDropdownSelect = (option) => {
    const selectedOption = reimbursementDropdownOptions.find(
      (item) => item.label === option
    );
    setSelectedReimbursementCoverage(selectedOption);

    if (selectedOption.name === "percent") {
      setReimbursementCoverageType("percent");
    } else if (selectedOption.name === "fixed") {
      setReimbursementCoverageType("fixed");
    }
  };

  const handleCoverageChange = (e) => {
    const inputValue = e.target.value;

    // Allow only valid number inputs with optional decimal
    if (/^\d*\.?\d*$/.test(inputValue)) {
      let newCoverage = inputValue === "" ? 0 : Number(inputValue);

      // Limit percent to max 100
      if (reimbursementCoverageType === "percent" && newCoverage > 100) {
        newCoverage = 100;
      }

      setReimbursementCoverage(newCoverage);
    }
  };

  const discountHandler = () => {
    setIsDiscountOpen(!isDiscountOpen);

    if (isDiscountOpen) {
      setDiscount(0);
      setSelectedDiscount(dropdownOptions[2]);
    } else {
      setDiscount(0);
      setSelectedDiscount(dropdownOptions[0]);
    }
  };

  // USE EFFECT
  useEffect(() => {
    // console.log("LOADED MODAL");
    if (service) {
      console.log("service", service);

      setPrice(service.price);
      setQty(service.quantity);
      setTempQty(service.quantity);
      setSelectedTeeth(service.teeth);
      setDiscount(service.discountValue);
      setSelectedDiscount(
        dropdownOptions.find((item) => item.name === service.discountType)
      );
      setDiscountedPrice(service.discountedPrice);
      setReimbursementCode(service.defaultPublicReimbursementCode);

      if (service.reimbursement?.coverageType === "percent") {
        setReimbursementCoverageType("percent");
        setSelectedReimbursementCoverage(
          reimbursementDropdownOptions.find((item) => item.name === "percent")
        );
      } else if (service.reimbursement?.coverageType === "fixed") {
        setReimbursementCoverageType("fixed");
        setSelectedReimbursementCoverage(
          reimbursementDropdownOptions.find((item) => item.name === "fixed")
        );
      } else {
        setReimbursementCoverageType(
          service.reimbursement?.coverageType || "percent"
        );
        setSelectedReimbursementCoverage(
          reimbursementDropdownOptions.find(
            (item) => item.name === service.reimbursement?.coverageType
          ) || reimbursementDropdownOptions[0]
        );
      }
      if (isPriceVariations) {
        setIsServicePriceVariations(true);
      } else {
        setIsServicePriceVariations(false);
      }

      if (service.discountValue > 0) {
        setIsDiscountOpen(true);
      }

      if (service.reimbursement && service.reimbursement.coverage > 0) {
        setIsReimbursement(true);
        setReimbursementType(service.reimbursement.type || "public");
        setReimbursementName(service.reimbursement.name || "");
        setReimbursementCode(service.reimbursement.code || "");
        setReimbursementBand(service.reimbursement.band || "");
        setReimbursementCoverageType(service.reimbursement.coverageType);
        setReimbursementCoverage(service.reimbursement.coverage || 0); // Ensure coverage is a number
      }
    }
  }, [service]);

  const currencySymbol = useCurrencySymbol(currency);

  useEffect(() => {
    // Update placeholders when reimbursementType or operatingCountry changes
    if (reimbursementType === "public") {
      if (operatingCountry === "US") {
        setNamePlaceholder(t("reimbursement.reimbursementNamePlaceholderUS"));
        setCodePlaceholder(t("reimbursement.reimbursementCodePlaceholderUS"));
      } else if (operatingCountry === "GB") {
        setNamePlaceholder(t("reimbursement.reimbursementNamePlaceholderGB"));
        setCodePlaceholder(t("reimbursement.reimbursementCodePlaceholderGB"));
      } else if (operatingCountry === "LT") {
        setNamePlaceholder(t("reimbursement.reimbursementNamePlaceholderLT"));
        setCodePlaceholder(t("reimbursement.reimbursementCodePlaceholderLT"));
      } else {
        setNamePlaceholder(t("placeholder.reimbursementName"));
        setCodePlaceholder(t("placeholder.reimbursementCode"));
      }
    } else {
      setNamePlaceholder(t("placeholder.insurerName"));
      setCodePlaceholder(t("placeholder.reimbursementCode"));
    }
  }, [reimbursementType, operatingCountry, t]);

  // PRICE CALCULATIONS FOR REIMBURSEMENT
  const unitPrice = discount > 0 ? discountedPrice : price;
  const totalPrice = isServicePriceVariations
    ? unitPrice * selectedTeeth.length
    : unitPrice * tempQty;

  // let reimbursedAmount = 0;

  if (isReimbursement) {
    if (reimbursementCoverageType === "percent") {
      reimbursedAmount = (reimbursementCoverage / 100) * totalPrice;
    } else if (reimbursementCoverageType === "fixed") {
      reimbursedAmount =
        reimbursementCoverage *
        (isServicePriceVariations ? selectedTeeth.length : tempQty);
    }
  }

  const patientCopay = totalPrice - reimbursedAmount;

  const computedReimbursedAmount = isReimbursement
    ? reimbursementCoverageType === "percent"
      ? (reimbursementCoverage / 100) * totalPrice
      : reimbursementCoverage *
        (isServicePriceVariations ? selectedTeeth.length : tempQty)
    : 0;

  const computedPatientCopay = totalPrice - computedReimbursedAmount;

  // LOGS
  // console.log("ORIGINAL SERVICE PRICE", service.price);
  // console.log("PRICE", price);
  // console.log("DISCOUNT", discount);
  // console.log("DISCOUNTED PRICE", discountedPrice);

  // console.log("service", service);

  // console.log("reimbursementCoverageType", reimbursementCoverageType);
  // console.log("reimbursementCoverage", reimbursementCoverage);
  // console.log("computedReimbursedAmount", computedReimbursedAmount);
  // console.log("computedPatientCopay", computedPatientCopay);
  // console.log("isReimbursement", isReimbursement);

  // console.log("isServicePriceVariations", isServicePriceVariations);
  // console.log("selectedTeeth", selectedTeeth);
  // console.log("tempQty", tempQty);

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay />
        <ModalContent
          borderRadius={{ base: "0", md: "1.5rem" }}
          w={{ base: "100%", md: "600px" }}
          minH={{ base: "100%", md: "auto" }}
          maxW="100vw"
          maxH={["100%", "90vh"]}
          m={0}
          border={`1px solid ${newBorder}`}
        >
          <ModalHeader p="1rem 1.5rem" borderBottom={`1px solid ${newBorder}`}>
            <Flex w="100%" align="center" justify="space-between">
              <Text fontSize="1rem" fontWeight="600" color={textPrimary}>
                {/* {t("service.changePriceTitle")} */}
                {t("button.edit")} - {service.service.name}
              </Text>
              <IconButton
                size="sm"
                bg={backgroundLight}
                color={textSecondary}
                onClick={handleClose}
                borderRadius="10rem"
                _hover={{ bg: primaryAlt, color: white }}
              >
                <Icon as={FaXmark} />
              </IconButton>
            </Flex>
          </ModalHeader>

          <ModalBody p="0 1.5rem" overflow="auto" h="100%">
            <Flex
              direction="column"
              w="100%"
              align="center"
              justify="center"
              p="0 0rem"
            >
              <Flex
                w="100%"
                align="center"
                justify="center"
                p="1.5rem 0"
                // bg="green"
              >
                {isServicePriceVariations ? (
                  <Flex
                    w="100%"
                    direction="column"
                    align="center"
                    justify="center"
                  >
                    <Text
                      fontSize="sm"
                      color={textPrimary}
                      fontWeight="500"
                      m="0 0 0.5rem"
                    >
                      {t("service.toothArea")}
                    </Text>
                    {!isMobile ? (
                      <TeethPopoverNew
                        handleToothClick={handleToothClick}
                        selectedTeeth={selectedTeeth}
                        setSelectedTeeth={setSelectedTeeth}
                        service={service}
                        saveEditedService={saveEditedService}
                        initialTeeth={service.teeth}
                      />
                    ) : (
                      <TeethPopoverNewMobile
                        handleToothClick={handleToothClick}
                        selectedTeeth={selectedTeeth}
                        setSelectedTeeth={setSelectedTeeth}
                        service={service}
                        saveEditedService={saveEditedService}
                        initialTeeth={service.teeth}
                      />
                    )}
                  </Flex>
                ) : (
                  <Flex align="center" justify="center" direction="column">
                    <Text
                      fontSize="sm"
                      color={textPrimary}
                      fontWeight="500"
                      m="0 0 0.5rem"
                    >
                      {t("service.qty")}
                    </Text>
                    <Flex align="center" justify="center">
                      <IconButton
                        icon={<FaMinus />}
                        fontSize="xs"
                        aria-label="Decrement"
                        onClick={(e) => {
                          e.stopPropagation();
                          decrementQty();
                        }}
                        isDisabled={tempQty <= 1}
                        bg={backgroundLight}
                        size="sm"
                        borderRadius="10rem"
                        color={textPrimary}
                        _hover={{ bg: primaryAlt, color: white }}
                        // Additional styling can be added here
                      />
                      <Input
                        placeholder="Enter quantity"
                        size={["lg", "lg"]}
                        fontSize={["md", "sm"]}
                        fontWeight="500"
                        min={1}
                        color={textPrimary}
                        type="text"
                        maxWidth="100px"
                        value={tempQty}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          const inputValue = e.target.value;
                          // Allow only numeric input or empty input, and ensure the minimum value is 1
                          if (/^\d*$/.test(inputValue)) {
                            const newValue =
                              inputValue === "" ? 0 : Number(inputValue);
                            setTempQty(newValue < 1 ? 1 : newValue); // Prevent value from going below 1
                          }
                        }}
                        mx={2} // Adds margin to the left and right of the input
                        textAlign="center" // Centers the text in the input
                        borderRadius="10rem"
                        // Additional styling can be added here
                        _hover={{
                          borderColor: primary,
                        }}
                        _focus={{
                          boxShadow: "none",
                          borderColor: primaryAlt,
                        }}
                      />

                      <IconButton
                        aria-label="Increment"
                        icon={<FaPlus />}
                        fontSize="xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          incrementQty();
                        }}
                        bg={backgroundLight}
                        color={textPrimary}
                        size="sm"
                        borderRadius="10rem"
                        _hover={{ bg: primaryAlt, color: white }}
                        // Additional styling can be added here
                      />
                    </Flex>
                  </Flex>
                )}
              </Flex>

              <Flex
                borderTop={`0px solid ${newBorder}`}
                w="100%"
                p={["0.5rem 0rem 2rem", "0rem 3rem 2rem"]}
                // bg="blue"
              >
                <Flex
                  align="center"
                  justify={["space-between", "center"]}
                  w="100%"
                  gap="1rem"
                  direction={["column", "row"]}
                >
                  <Flex
                    align={isDiscountOpen ? "flex-start" : "center"}
                    justify={isDiscountOpen ? "flex-start" : "center"}
                    direction="column"
                    m={isDiscountOpen ? "0" : "0 auto"}
                    w="100%"
                    maxW={isDiscountOpen ? (isMobile ? "100%" : "40%") : "100%"}
                  >
                    <Flex
                      align="center"
                      m={isDiscountMenuOpen ? "0 auto 0.5rem" : "0 auto 0.5rem"}
                    >
                      <Text fontSize="sm" color={textPrimary} fontWeight="500">
                        {t("service.servicePrice")}
                      </Text>

                      {(price !== service.service.price ||
                        service.priceRange) && (
                        <Tooltip
                          p="0.5rem 1rem"
                          borderRadius="1rem"
                          label={
                            <Flex direction="column">
                              {service.priceRange ? (
                                <Flex align="center">
                                  <Text fontSize="sm" color={whiteAlt}>
                                    {t("service.serviceOriginalPrice")}:
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    color={white}
                                    ml="0.25rem"
                                  >
                                    {" "}
                                    {currencySymbol}{" "}
                                    {service &&
                                      service.service &&
                                      service.service.price &&
                                      service.service.price.toFixed(2)}{" "}
                                    -{" "}
                                    {service &&
                                      service.maxPrice &&
                                      service.maxPrice.toFixed(2)}{" "}
                                  </Text>
                                </Flex>
                              ) : (
                                <Flex align="center">
                                  <Text fontSize="sm" color={whiteAlt}>
                                    {t("service.serviceOriginalPrice")}:
                                  </Text>
                                  <Text
                                    fontSize="sm"
                                    color={white}
                                    ml="0.25rem"
                                  >
                                    {currencySymbol}{" "}
                                    {service &&
                                      service.service &&
                                      service.service.price &&
                                      service.service.price.toFixed(2)}
                                  </Text>
                                </Flex>
                              )}
                            </Flex>
                          }
                          placement="top"
                        >
                          <Flex align="center" justify="center">
                            <Icon
                              as={FaCircleQuestion}
                              color={color8}
                              ml="0.5rem"
                              fontSize="1rem"
                              _hover={{
                                cursor: "pointer",
                              }}
                            />
                          </Flex>
                        </Tooltip>
                      )}
                    </Flex>

                    <Flex
                      align="center"
                      gap="0.5rem"
                      w={["100%", "initial"]}
                      // maxW="11.25rem"
                      // pl={isDiscountOpen ? "0rem" : "2.25rem"}
                    >
                      <InputGroup
                        bg={backgroundLight}
                        border={
                          isPriceEditable
                            ? `1px solid #e2e8f0`
                            : `1px solid #e2e8f0`
                        }
                        borderRadius="10rem"
                        position="relative"
                        role="group"
                        _hover={{
                          borderColor: primary,
                          cursor: "pointer",
                        }}
                        _focusWithin={{
                          borderColor: primaryAlt,
                        }}
                      >
                        <Input
                          size={["lg", "lg"]}
                          minW={
                            isDiscountOpen
                              ? isMobile
                                ? "100%"
                                : "120px"
                              : "11.25rem"
                          }
                          maxW={
                            isDiscountOpen
                              ? "100%"
                              : isMobile
                              ? "100%"
                              : "11.25rem"
                          }
                          fontSize={["md", "sm"]}
                          fontWeight="500"
                          type="text"
                          bg={isPriceEditable ? white : backgroundLight}
                          color={textPrimary}
                          border="none"
                          boxShadow="none"
                          value={
                            isPriceEditable
                              ? price
                              : `${currencySymbol} ${price}`
                          }
                          onChange={isPriceEditable ? handlePriceChange : null}
                          placeholder={t("service.price")}
                          borderRadius="10rem"
                          textAlign="center"
                          isDisabled={!isPriceEditable}
                          opacity="1"
                          // _hover={{
                          //   cursor: "default",
                          //   pointerEvents: "none",
                          // }}
                          _disabled={{
                            bg: backgroundLight,
                            color: textPrimary,
                          }}
                          _focusVisible={{
                            boxShadow: "none",
                          }}
                        />
                        <Flex
                          align="center"
                          position="absolute"
                          right="0.5rem"
                          top="50%"
                          transform="translateY(-50%)"
                          zIndex="1"
                        >
                          <Tooltip
                            p="0.5rem 1rem"
                            borderRadius="1rem"
                            label={t("service.giveDiscount")}
                            placement="top"
                          >
                            <IconButton
                              size="sm"
                              bg={
                                isDiscountOpen
                                  ? primaryAlt
                                  : isPriceEditable
                                  ? backgroundLight
                                  : white
                              }
                              color={isDiscountOpen ? white : textSecondary}
                              borderRadius="10rem"
                              onClick={() => discountHandler()}
                              _hover={{
                                bg: isDiscountOpen ? primaryAlt : primaryAlt,
                                color: isDiscountOpen ? white : white,
                              }}
                            >
                              <Icon as={TbPercentage} fontSize="0.875rem" />
                            </IconButton>
                          </Tooltip>
                        </Flex>
                      </InputGroup>
                    </Flex>
                  </Flex>
                  {isDiscountOpen && (
                    <Flex
                      as={motion.div}
                      initial={{ opacity: 0, height: "auto" }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: "auto" }}
                      // p="0.5rem 0"
                      pl={["0rem", "2rem"]}
                      w={["100%", "initial"]}
                      direction="column"
                    >
                      <Flex
                        align="flex-start"
                        justify="flex-start"
                        w={["100%", "initial"]}
                        direction="column"
                        pl="0.5rem"
                      >
                        <Text
                          fontSize="sm"
                          color={textPrimary}
                          fontWeight="500"
                          m="0 auto 0.5rem"
                          // mr="0.5rem"
                          // p="0.5rem 0 0"
                        >
                          {t("service.serviceDiscount")}
                        </Text>
                        <Flex w="100%">
                          <InputGroup
                            border={`1px solid #e2e8f0`}
                            borderRadius="10rem"
                            // pr="0.25rem"
                            position="relative"
                            role="group"
                            transition="0.2s all"
                            _hover={{
                              borderColor: primary,
                            }}
                            _focusWithin={{
                              borderColor: primaryAlt,
                            }}
                          >
                            <Input
                              size={["lg", "lg"]}
                              w="100%"
                              // maxW="80px"
                              minW={["80px", "80px"]}
                              fontSize={["md", "sm"]}
                              fontWeight="500"
                              type="text"
                              min={0}
                              max={99}
                              value={discount}
                              onChange={handleDiscountChange}
                              placeholder=""
                              textAlign="center"
                              border="none"
                              borderRadius="10rem"
                              // onFocus={(e) => e.target.select()}
                              // _hover={{
                              //   borderColor: primary,
                              // }}
                              _focusVisible={{
                                // boxShadow: `0 0 0 1px ${primaryAlt}`,
                                boxShadow: "none",
                              }}
                            />
                            <Flex
                              position="relative"
                              right={["0.5rem", "0.5rem"]}
                              top={["0.5rem", "0.5rem"]}
                              flexShrink="0"
                            >
                              <DropdownButtonNew
                                initialLabel={
                                  selectedDiscount &&
                                  selectedDiscount.label &&
                                  selectedDiscount.label
                                }
                                items={dropdownOptions.map(
                                  (item) => item.label
                                )}
                                onSelectItem={handleDropdownSelect}
                              />
                            </Flex>
                          </InputGroup>
                        </Flex>
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </Flex>

              {/* REIMBURSEMENT START */}
              <Flex
                w="100%"
                align="center"
                p={["0rem 0rem 0.75rem", "0rem 3rem 0.75rem"]}
              >
                <Switch
                  size="sm"
                  colorScheme="primary"
                  isChecked={isReimbursement}
                  onChange={(e) => setIsReimbursement(e.target.checked)}
                  mr="0.5rem"
                />
                <Text fontSize="0.8rem" color={textSecondary} fontWeight="500">
                  {t("service.reimbursement")}
                </Text>
              </Flex>

              {/* REIMBURSEMENT END */}

              {isReimbursement && (
                <Flex
                  borderTop={`0px solid ${newBorder}`}
                  w="100%"
                  p={["0rem 0rem 2rem", "0.5rem 3rem 2rem"]}
                  // bg="blue"
                  direction="column"
                >
                  <Flex
                    align="center"
                    justify={["space-between", "center"]}
                    w="100%"
                    // gap="1rem"
                    direction={["column", "column"]}
                  >
                    <Flex
                      align="center"
                      justify="space-between"
                      w="100%"
                      gap="1rem"
                    >
                      <FormControlReimbursementType
                        label={t("form.reimbursementType")}
                        labelFontSize="0.8rem"
                        value={reimbursementType}
                        placeholder={t("placeholder.selectReimbursementType")}
                        onChange={(selectedOption) => {
                          setReimbursementType(selectedOption);
                        }}
                        isRequired={true}
                        w={["100%", "50%"]}
                        operatingCountry={operatingCountry}
                      />
                      <FormControlDefault
                        label={t("form.reimbursementName")}
                        labelFontSize="0.8rem"
                        size="lg"
                        value={reimbursementName}
                        placeholder={namePlaceholder}
                        onChange={(e) => setReimbursementName(e.target.value)}
                        isRequired={true}
                        w={["100%", "50%"]}
                      />
                    </Flex>

                    <Flex
                      align="flex-start"
                      justify="flex-start"
                      w={["100%", "100%"]}
                      direction="column"
                    >
                      <Flex
                        align="center"
                        justify="space-between"
                        w="100%"
                        gap="1rem"
                      >
                        <FormControlDefault
                          label={t("form.reimbursementCode")}
                          labelFontSize="0.8rem"
                          size="lg"
                          value={reimbursementCode}
                          placeholder={codePlaceholder}
                          onChange={(e) => setReimbursementCode(e.target.value)}
                          isRequired={true}
                          w={["100%", "50%"]}
                          mb="0"
                        />

                        <Flex
                          w="50%"
                          direction="column"
                          align="flex-start"
                          justify="center"
                        >
                          <Text
                            fontSize="0.8rem"
                            color={textPrimary}
                            fontWeight="500"
                            textAlign="left"
                            m="0 0 6px"
                          >
                            {t("form.reimbursementCoverage")}
                          </Text>
                          <InputGroup
                            border={`1px solid #e2e8f0`}
                            borderRadius="0.875rem"
                            // pr="0.25rem"
                            position="relative"
                            role="group"
                            transition="0.2s all"
                            _hover={{
                              borderColor: "##cbd5e0",
                            }}
                            _focusWithin={{
                              borderColor: primaryAlt,
                            }}
                          >
                            <Input
                              size={["lg", "lg"]}
                              w="100%"
                              // maxW="80px"
                              minW={["80px", "80px"]}
                              fontSize={["md", "sm"]}
                              fontWeight="500"
                              type="text"
                              min={0}
                              max={99}
                              value={reimbursementCoverage}
                              onChange={handleCoverageChange}
                              placeholder=""
                              textAlign="center"
                              border="none"
                              borderRadius="10rem"
                              // onFocus={(e) => e.target.select()}
                              // _hover={{
                              //   borderColor: primary,
                              // }}
                              _focusVisible={{
                                // boxShadow: `0 0 0 1px ${primaryAlt}`,
                                boxShadow: "none",
                              }}
                            />
                            <Flex
                              position="relative"
                              right={["0.5rem", "0.5rem"]}
                              top={["0.5rem", "0.5rem"]}
                              flexShrink="0"
                            >
                              <DropdownButtonNew
                                initialLabel={
                                  selectedReimbursementCoverage &&
                                  selectedReimbursementCoverage.label
                                    ? selectedReimbursementCoverage.label
                                    : t("reimbursement.coverageType")
                                }
                                items={reimbursementDropdownOptions.map(
                                  (item) => item.label
                                )}
                                onSelectItem={handleReimbursementDropdownSelect}
                              />
                            </Flex>
                          </InputGroup>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              )}

              <Flex
                borderTop={`0px solid ${newBorder}`}
                w="100%"
                p={["0rem 0rem 1.5rem", "0rem 3rem 1.5rem"]}
                // bg="yellow"
              >
                <Flex
                  w="100%"
                  direction="column"
                  bg={backgroundLight}
                  borderRadius="1rem"
                  align="center"
                  p="1.5rem 1.5rem"
                  gap="0.5rem"

                  // borderTop={`1px solid ${newBorder}`}
                >
                  <Flex w="100%" align="center" justify="space-between">
                    <Text
                      // fontSize="0.8rem"
                      fontSize="sm"
                      color={textSecondary}
                      fontWeight="500"
                    >
                      {t("service.servicePrice")}:
                    </Text>
                    <Flex align="center">
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={discount ? textSecondary : textPrimary}
                        fontWeight="500"
                        textDecor={discount > 0 ? "line-through" : "none"}
                      >
                        {currencySymbol} {price.toFixed(2)}
                      </Text>

                      {discount > 0 && (
                        <Text
                          as="span"
                          fontSize="sm"
                          color={textPrimary}
                          fontWeight="500"
                          ml="0.75rem"
                          textDecor="none"
                        >
                          {currencySymbol} {discountedPrice.toFixed(2)}
                        </Text>
                      )}
                    </Flex>
                  </Flex>

                  {isServicePriceVariations ? (
                    <Flex w="100%" align="center" justify="space-between">
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textSecondary}
                        fontWeight="500"
                      >
                        {t("service.teethCount")}:
                      </Text>
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textPrimary}
                        fontWeight="500"
                      >
                        {selectedTeeth.length} {t("common.qty")}
                      </Text>
                    </Flex>
                  ) : (
                    <Flex w="100%" align="center" justify="space-between">
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textSecondary}
                        fontWeight="500"
                      >
                        {t("service.qty")}:
                      </Text>
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textPrimary}
                        fontWeight="500"
                      >
                        {tempQty} {t("common.qty")}
                      </Text>
                    </Flex>
                  )}

                  {isDiscountOpen && discount > 0 && (
                    <Flex
                      w="100%"
                      align="center"
                      justify="space-between"
                      as={motion.div}
                      initial={{ opacity: 1, height: "auto" }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 1, height: "auto" }}
                    >
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textSecondary}
                        fontWeight="500"
                      >
                        {t("service.discountTotal")}:
                      </Text>
                      {isServicePriceVariations ? (
                        <Text
                          // fontSize="0.8rem"
                          fontSize="sm"
                          color={color6}
                          fontWeight="500"
                        >
                          {selectedDiscount.name === "percentage" ? (
                            <>
                              -{currencySymbol}{" "}
                              {(
                                discountedAmount(price, discount) *
                                selectedTeeth.length
                              ).toFixed(2)}{" "}
                            </>
                          ) : (
                            <>
                              -{currencySymbol}{" "}
                              {(
                                discountedDeductAmount(
                                  price,
                                  discountedPrice,
                                  discount
                                ) * selectedTeeth.length
                              ).toFixed(2)}{" "}
                            </>
                          )}
                        </Text>
                      ) : (
                        <Text
                          // fontSize="0.8rem"
                          fontSize="sm"
                          color={color6}
                          fontWeight="500"
                        >
                          {selectedDiscount.name === "percentage" ? (
                            <>
                              {" "}
                              -{currencySymbol}{" "}
                              {(
                                discountedAmount(price, discount) * tempQty
                              ).toFixed(2)}{" "}
                            </>
                          ) : (
                            <>
                              -{currencySymbol}{" "}
                              {(
                                discountedDeductAmount(
                                  price,
                                  discountedPrice,
                                  discount
                                ) * tempQty
                              ).toFixed(2)}{" "}
                            </>
                          )}
                        </Text>
                      )}
                    </Flex>
                  )}

                  <Flex w="100%" align="center" justify="space-between">
                    <Text
                      // fontSize="0.8rem"
                      fontSize="sm"
                      color={textSecondary}
                      fontWeight="500"
                    >
                      {t("service.serviceTotalPrice")}:
                    </Text>

                    {discount > 0 ? (
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textPrimary}
                        fontWeight="500"
                      >
                        {isServicePriceVariations ? (
                          <Text as="span">
                            {currencySymbol}{" "}
                            {(discountedPrice * selectedTeeth.length).toFixed(
                              2
                            )}{" "}
                          </Text>
                        ) : (
                          <Text as="span">
                            {" "}
                            {currencySymbol}{" "}
                            {(discountedPrice * tempQty).toFixed(2)}
                          </Text>
                        )}
                      </Text>
                    ) : (
                      <Text
                        // fontSize="0.8rem"
                        fontSize="sm"
                        color={textPrimary}
                        fontWeight="500"
                      >
                        {isServicePriceVariations ? (
                          <Text as="span">
                            {currencySymbol}{" "}
                            {(price * selectedTeeth.length).toFixed(2)}
                          </Text>
                        ) : (
                          <Text as="span">
                            {currencySymbol} {(price * tempQty).toFixed(2)}
                          </Text>
                        )}
                      </Text>
                    )}
                  </Flex>

                  {isReimbursement && (
                    <>
                      <Flex w="100%" align="center" justify="space-between">
                        <Text
                          fontSize="sm"
                          color={textSecondary}
                          fontWeight="500"
                        >
                          {t("reimbursement.reimbursedAmount")}:
                        </Text>
                        <Text fontSize="sm" color={primary} fontWeight="500">
                          {currencySymbol} {computedReimbursedAmount.toFixed(2)}
                        </Text>
                      </Flex>

                      <Flex w="100%" align="center" justify="space-between">
                        <Text
                          fontSize="sm"
                          color={textSecondary}
                          fontWeight="500"
                        >
                          {t("reimbursement.patientCopay")}:
                        </Text>
                        <Text
                          fontSize="sm"
                          color={textPrimary}
                          fontWeight="600"
                        >
                          {currencySymbol} {computedPatientCopay.toFixed(2)}
                        </Text>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Flex
              w="100%"
              borderTop={`0px solid ${newBorder}`}
              p="0rem 1.5rem 1.5rem"
              bg="transparent"
              position="relative"
            >
              <Flex
                w="100%"
                align="center"
                justify={["space-between", "center"]}
                gap="1rem"
              >
                <Button
                  size={["lg", "lg"]}
                  w={["50%", "auto"]}
                  fontSize={["sm", "md"]}
                  borderRadius="10rem"
                  onClick={handleClose}
                  bg={backgroundLight}
                  color={textSecondary}
                >
                  {t("common.cancel")}
                </Button>
                <Tooltip
                  p="0.5rem 1rem"
                  borderRadius="1rem"
                  fontSize="xs"
                  label={
                    <>
                      <Flex>{t("common.enterRequiredFields")}</Flex>
                    </>
                  }
                  placement="top"
                  isDisabled={
                    reimbursementType &&
                    reimbursementName &&
                    reimbursementCode &&
                    reimbursementCoverage
                  }
                >
                  <Button
                    px="1.5rem"
                    size={["lg", "lg"]}
                    w={["50%", "auto"]}
                    fontSize={["sm", "md"]}
                    borderRadius="10rem"
                    bg={textPrimary}
                    color={white}
                    onClick={handleSubmitChanges}
                    _hover={{ bg: primaryAlt }}
                    isDisabled={
                      (isServicePriceVariations &&
                        selectedTeeth.length === 0) ||
                      (isReimbursement &&
                        (!reimbursementType ||
                          !reimbursementName ||
                          !reimbursementCode ||
                          !reimbursementCoverage))
                    }
                  >
                    {t("button.save")}
                  </Button>
                </Tooltip>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PriceQuantityPopoverModal;
